// Helper: preserve the GoKwik checkout wrapper element across innerHTML replacements.
// GoKwik's SDK attaches its state (event listeners, internal button reference) to the
// original button element at load time. If we destroy that element by replacing innerHTML,
// clicking the hidden button afterwards is a no-op — modal never opens.
// This helper detaches the element before the replacement, then swaps it back in.
function preserveGokwikCheckout(oldContainer, replaceFn) {
  if (!oldContainer) { replaceFn(); return; }
  const preserved = oldContainer.querySelector('.gokwik-checkout');
  replaceFn();
  if (!preserved) return;
  const placeholder = oldContainer.querySelector('.gokwik-checkout');
  // Only swap when the new markup actually has a slot for it. If it does not
  // (the cart just emptied, so no footer was rendered), drop the preserved node
  // instead of appending it — appending would leave a stray visible checkout
  // button at the root of the empty drawer.
  if (placeholder && placeholder !== preserved) {
    placeholder.replaceWith(preserved);
  }
}

// Helper: sync the cart drawer footer across a re-render.
// The footer is only server-rendered when the cart is non-empty, so a plain
// "replace innerHTML when both sides exist" check gets two transitions wrong:
// removing the last item leaves a stale "Total: ₹0" footer with a live checkout
// button, and going from empty to filled never inserts the footer at all.
// Handle all four combinations explicitly.
function syncCartDrawerFooter(newRoot, scope) {
  const root = scope || document;
  const newFooter = newRoot.querySelector(".wt-cart__drawer__footer");
  const oldFooter = root.querySelector(".wt-cart__drawer__footer");

  if (newFooter && oldFooter) {
    preserveGokwikCheckout(oldFooter, () => {
      oldFooter.innerHTML = newFooter.innerHTML;
    });
  } else if (newFooter && !oldFooter) {
    const inner = root.querySelector(".wt-cart__drawer__inner");
    if (inner) inner.insertAdjacentHTML("beforeend", newFooter.outerHTML);
  } else if (!newFooter && oldFooter) {
    oldFooter.remove();
  }
}

// Helper: keep the cross-sell block in step with the cart state.
// Like the footer, it is only server-rendered while the cart has items, so removing
// the last item used to leave a stale recommendations slider on the empty drawer
// (.wt-cart--empty does not hide it).
// replaceExisting is opt-in: a quantity change should not rebuild the slider, since
// that flickers and refetches recommendations — it only needs the removal case.
function syncCartDrawerCrossSell(newRoot, scope, replaceExisting) {
  const root = scope || document;
  const newCrossSell = newRoot.querySelector(".wt-cart__cross-sell, .sokora-routine-section");
  const oldCrossSell = root.querySelector(".wt-cart__cross-sell, .sokora-routine-section");

  if (newCrossSell && oldCrossSell) {
    if (!replaceExisting) return;
    oldCrossSell.outerHTML = newCrossSell.outerHTML;
    window.initSokoraCrossSellSlider?.();
  } else if (newCrossSell && !oldCrossSell) {
    const body = root.querySelector(".wt-cart__drawer__body");
    if (body) body.insertAdjacentHTML("beforeend", newCrossSell.outerHTML);
    window.initSokoraCrossSellSlider?.();
  } else if (!newCrossSell && oldCrossSell) {
    oldCrossSell.remove();
  }
}

class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener('cart-drawer:refresh', (e) => this.refreshCartDrawer(e))
      return;
    }

    this.init();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
        this.closeButton().focus();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      this.mainTrigger.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.closeButton().focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");

      // Force Swiper instances inside the drawer to recalculate/initialize when drawer becomes visible
      setTimeout(() => {
        this.querySelectorAll("slideshow-section").forEach((slider) => {
          if (slider.swiper) {
            slider.swiper.update();
            slider.centerNavigation?.();
          } else {
            slider.swiperInitilize?.();
          }
        });
      }, 150);
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });

    document.addEventListener('cart-drawer:refresh', (e) => this.refreshCartDrawer(e))
  }

  renderContents(parsedState, isClosedCart = true) {
    const addingCard = this.querySelector(".shoppable-product-card.is-adding");

    if (addingCard && parsedState.sections["cart-drawer"]) {
      // MULTI-STAGE PRECISE UPDATE FLOW:
      const parser = new DOMParser();
      const newDoc = parser.parseFromString(parsedState.sections["cart-drawer"], "text/html");
      const newInner = newDoc.querySelector("#CartDrawer");

      if (newInner) {
        // Stage 1: Update Header, Items, and Footer instantly so the item appears in the cart first!
        const selectorsToUpdate = [
          "#CartDrawer-TotalQty",
          ".wt-free-shipping-bar",
          "#CartDrawer-CartItems",
          ".wt-cart__drawer__footer",
          "#cart-errors"
        ];

        selectorsToUpdate.forEach((selector) => {
          if (selector === ".wt-cart__drawer__footer") {
            syncCartDrawerFooter(newInner, this);
            return;
          }
          const oldEl = this.querySelector(selector);
          const newEl = newInner.querySelector(selector);
          if (oldEl && newEl) {
            oldEl.innerHTML = newEl.innerHTML;
          }
        });

        // Also update the cart icon bubble instantly
        const bubbleSection = this.getSectionsToRender().find(s => s.id === "cart-icon-bubble");
        if (bubbleSection && parsedState.sections["cart-icon-bubble"]) {
          const bubbleEl = document.getElementById(bubbleSection.id);
          if (bubbleEl) {
            bubbleEl.innerHTML = this.getSectionInnerHTML(
              parsedState.sections["cart-icon-bubble"],
              bubbleSection.selector
            );
          }
        }

        // Stage 2: Smoothly fade out the added product card in the slider
        addingCard.style.setProperty("opacity", "0", "important");
        addingCard.style.setProperty("transform", "scale(0.85)", "important");
        addingCard.style.transition = "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)";

        // Stage 3: After the card has faded out, update the cross-sell slider HTML
        setTimeout(() => {
          syncCartDrawerCrossSell(newInner, this, true);
        }, 550);
      }
    } else {
      // Default instantaneous update behavior for standard cart actions
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (sectionElement) {
          preserveGokwikCheckout(sectionElement, () => {
            sectionElement.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.id],
              section.selector,
            );
          });
        }
      });
    }

    if (isClosedCart) {
      setTimeout(() => {
        this.toggleDrawerClasses();
        if (this.isOpen) {
          this.closeButton().focus();
        }
      });
    }

    // Notify swap widget to re-initialize after cart contents update
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('cart-drawer:refresh-complete'));
    }, 100);
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  refreshCartDrawer(e){
    const sectionsToRender = this.getSectionsToRender();
    fetch(`${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`)
    .then(response => response.json())
    .then((response) => {
      const parsedState = {
        "sections": response
      }
      this.renderContents(parsedState, false);
      document.dispatchEvent(new CustomEvent('cart-drawer:refresh-complete'));
    })
    .catch(e => {
      console.log(e)
    })
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawerSection);

class CartDrawerItems extends CartItems {
  onCartUpdate() {
    // Override parent to prevent redundant fetch calls
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }

  updateQuantity(line, quantity, name) {
    // Optimistic UI: update quantity display instantly
    const lineItem = document.getElementById(`CartDrawer-Item-${line}`);
    if (lineItem) {
      const input = lineItem.querySelector('.js-counter-quantity');
      if (input && quantity > 0) {
        input.value = quantity;
      }
    }

    // Minimal loading state - no spinner, just slight fade
    const mainCartItems = document.getElementById("CartDrawer-CartItems");
    if (mainCartItems) mainCartItems.style.opacity = "0.85";
    mainCartItems.style.transition = "opacity 0.15s";

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        if (parsedState.errors) {
          const quantityElement = document.getElementById(`Drawer-quantity-${line}`);
          if (quantityElement) quantityElement.value = quantityElement.getAttribute("value");
          return;
        }

        const cartDrawerWrapper = document.querySelector("cart-drawer");
        if (cartDrawerWrapper) {
          cartDrawerWrapper.classList.toggle("wt-cart--empty", parsedState.item_count === 0);
        }

        // Targeted update: only update specific parts, not full innerHTML
        const newDoc = new DOMParser().parseFromString(
          parsedState.sections["cart-drawer"], "text/html"
        );

        // Update header (cart count)
        const newHeader = newDoc.querySelector("#CartDrawer-TotalQty");
        const oldHeader = document.querySelector("#CartDrawer-TotalQty");
        if (newHeader && oldHeader) oldHeader.innerHTML = newHeader.innerHTML;

        // Update cart items
        const newItems = newDoc.querySelector("#CartDrawer-CartItems");
        const oldItems = document.querySelector("#CartDrawer-CartItems");
        if (newItems && oldItems) oldItems.innerHTML = newItems.innerHTML;

        // Update footer — handles empty/non-empty transitions and keeps GoKwik SDK state
        syncCartDrawerFooter(newDoc);

        // Update / remove cross-sell block when cart items change or cart empties
        syncCartDrawerCrossSell(newDoc, document, true);

        // Update free shipping bar
        const newShipping = newDoc.querySelector(".wt-free-shipping-bar");
        const oldShipping = document.querySelector(".wt-free-shipping-bar");
        if (newShipping && oldShipping) {
          oldShipping.outerHTML = newShipping.outerHTML;
        } else if (newShipping && !oldShipping) {
          const header = document.querySelector("cart-drawer header");
          if (header) header.insertAdjacentHTML('beforeend', newShipping.outerHTML);
        } else if (!newShipping && oldShipping) {
          oldShipping.remove();
        }

        // Update cart icon bubble
        if (parsedState.sections["cart-icon-bubble"]) {
          const bubbleEl = document.getElementById("cart-icon-bubble");
          if (bubbleEl) {
            const bubbleDoc = new DOMParser().parseFromString(
              parsedState.sections["cart-icon-bubble"], "text/html"
            );
            const newBubble = bubbleDoc.querySelector(".shopify-section");
            if (newBubble) bubbleEl.innerHTML = newBubble.innerHTML;
          }
        }

        // If item was removed (quantity 0), remove the element
        if (quantity == 0 && lineItem) {
          lineItem.remove();
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items" });
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        const items = document.getElementById("CartDrawer-CartItems");
        if (items) {
          items.style.opacity = "1";
          items.style.transition = "opacity 0.15s";
        }
      });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      ?.querySelector(selector)?.innerHTML;
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

// ==========================================================================
// SOKORA CROSS-SELL GLOBAL SLIDER CONTROLLER (DELEGATED)
// ==========================================================================
window.slideSokoraCrossSell = function (direction) {
  const slider = document.getElementById("sokora-cross-sell-slider") || document.querySelector(".sokora-routine-cards-list");
  if (!slider) return;

  const cards = Array.from(slider.querySelectorAll(".sokora-routine-card"));
  if (cards.length <= 1) return;

  const scrollLeft = slider.scrollLeft;
  let closestIndex = 0;
  let minDiff = Infinity;

  cards.forEach((card, i) => {
    const diff = Math.abs(card.offsetLeft - slider.offsetLeft - scrollLeft);
    if (diff < minDiff) {
      minDiff = diff;
      closestIndex = i;
    }
  });

  let targetIndex = closestIndex;
  if (direction === "next") {
    targetIndex = (closestIndex + 1) >= cards.length ? 0 : closestIndex + 1;
  } else if (direction === "prev") {
    targetIndex = (closestIndex - 1) < 0 ? cards.length - 1 : closestIndex - 1;
  }

  const targetCard = cards[targetIndex];
  if (targetCard) {
    const targetLeft = targetCard.offsetLeft - slider.offsetLeft;
    slider.scrollTo({
      left: targetLeft,
      behavior: "smooth"
    });
  }
};

document.addEventListener("click", function (e) {
  const nextBtn = e.target.closest(".sokora-routine-nav--next");
  if (nextBtn) {
    e.preventDefault();
    e.stopPropagation();
    window.slideSokoraCrossSell("next");
    return;
  }

  const prevBtn = e.target.closest(".sokora-routine-nav--prev");
  if (prevBtn) {
    e.preventDefault();
    e.stopPropagation();
    window.slideSokoraCrossSell("prev");
    return;
  }
});

let skTouchStartX = 0;
let skTouchStartY = 0;
let skTouchStartTime = 0;

document.addEventListener("touchstart", function (e) {
  const slider = e.target.closest("#sokora-cross-sell-slider, .sokora-routine-cards-list");
  if (!slider) return;
  if (e.target.closest("button, input, select, form")) return;
  const touch = e.touches[0];
  if (touch) {
    skTouchStartX = touch.clientX;
    skTouchStartY = touch.clientY;
    skTouchStartTime = Date.now();
  }
}, { passive: true });

document.addEventListener("touchend", function (e) {
  const slider = e.target.closest("#sokora-cross-sell-slider, .sokora-routine-cards-list");
  if (!slider) return;
  const touch = e.changedTouches[0];
  if (!touch) return;

  const deltaX = touch.clientX - skTouchStartX;
  const deltaY = touch.clientY - skTouchStartY;
  const duration = Date.now() - skTouchStartTime;

  if (Math.abs(deltaX) > Math.abs(deltaY) && (Math.abs(deltaX) > 25 || (Math.abs(deltaX) > 15 && duration < 250))) {
    if (deltaX < 0) {
      window.slideSokoraCrossSell("next");
    } else {
      window.slideSokoraCrossSell("prev");
    }
  }
}, { passive: true });

let skMouseStartX = 0;
let skMouseStartY = 0;
let skIsMouseDown = false;

document.addEventListener("mousedown", function (e) {
  const slider = e.target.closest("#sokora-cross-sell-slider, .sokora-routine-cards-list");
  if (!slider) return;
  if (e.target.closest("button, input, select, form")) return;
  skIsMouseDown = true;
  skMouseStartX = e.clientX;
  skMouseStartY = e.clientY;
});

document.addEventListener("mouseup", function (e) {
  if (!skIsMouseDown) return;
  skIsMouseDown = false;
  const slider = e.target.closest("#sokora-cross-sell-slider, .sokora-routine-cards-list");
  if (!slider) return;

  const deltaX = e.clientX - skMouseStartX;
  const deltaY = e.clientY - skMouseStartY;

  if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 25) {
    if (deltaX < 0) {
      window.slideSokoraCrossSell("next");
    } else {
      window.slideSokoraCrossSell("prev");
    }
  }
});
